import React, { useEffect } from "react";
import { ContactDto } from "@eagerdog/interfaces";
import { helperService } from "src/services/helper.service";

import DatePick from "src/components/DatePick/DatePick";
import Input from "src/components/Input/Input";

import styles from "./Session.module.scss";

interface IProps {
    index: number;
    sessionId?: string;
    start: Date;
    end: Date;
    regularCapacity: number;
    dropinCapacity: number;
    waitlistSize?: number;
    hideCapacity?: boolean;
    instructors: ContactDto[];
    onDelete(): void;
    className?: string;
    hideDelete?: boolean;
    onChange(SessionDto: { _id?: string; start_date: Date; end_date: Date; instructors: ContactDto[], regular_capacity: number, dropin_capacity: number, waitlist_size?: number }): void;
}

const Session: React.FC<IProps> = (props) => {
    const [start, setStart] = React.useState<Date>(props.start);
    const [end, setEnd] = React.useState<Date>(props.end);
    const [instructors, setInstructors] = React.useState<ContactDto[]>(props.instructors);
    const [regularCapacity, setRegularCapacity] = React.useState<number>(props.regularCapacity);
    const [dropinCapacity, setDropinCapacity] = React.useState<number>(props.dropinCapacity);
    const [waitlistSize, setWaitlistSize] = React.useState<number>(props.waitlistSize || 0);

    // Always up-to-date ref so the onChange effect can read current start
    // without adding start to its dependency array (which would cause stale-end issues).
    const startRef = React.useRef(start);
    startRef.current = start;

    // Sync state with props when they change (for editing existing courses).
    // All comparisons are value-based to avoid infinite loops from new object/array references.
    useEffect(() => {
        if (props.start?.getTime() !== start?.getTime()) setStart(props.start);
        if (props.end?.getTime() !== end?.getTime()) setEnd(props.end);
        if (JSON.stringify(props.instructors) !== JSON.stringify(instructors)) setInstructors(props.instructors);
        if (props.regularCapacity !== regularCapacity) setRegularCapacity(props.regularCapacity);
        if (props.dropinCapacity !== dropinCapacity) setDropinCapacity(props.dropinCapacity);
        if ((props.waitlistSize || 0) !== waitlistSize) setWaitlistSize(props.waitlistSize || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.start, props.end, props.instructors, props.regularCapacity, props.dropinCapacity, props.waitlistSize]);

    const updateInstructor = (index: number, field: keyof ContactDto, value: string) => {
        setInstructors((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value, contact_type: "Instructor", phone: "123", email: "email" };
            return updated;
        });
    };

    const pStart = helperService.usePrevious(start) as Date | undefined;

    // When start changes, auto-advance end by 1h.
    useEffect(() => {
        if (pStart !== undefined && start?.getTime() !== pStart?.getTime()) {
            setEnd(new Date(start.getTime() + 60 * 60 * 1000));
        }
    // `end` intentionally omitted — we only want this to fire when start changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start]);

    // Notify parent of session changes.
    // `start` is intentionally excluded from deps and read via startRef instead.
    // If start were a dep, this effect would fire in the same render as the pStart effect
    // above — before end has been auto-advanced — sending the parent a stale end value.
    // By excluding start, this only fires once end has actually settled (either from the
    // auto-advance or from the user manually picking an end date), guaranteeing the parent
    // always receives a consistent {start, end} pair.
    useEffect(() => {
        props.onChange({ _id: props.sessionId, start_date: startRef.current, end_date: end, instructors, regular_capacity: regularCapacity, dropin_capacity: dropinCapacity, waitlist_size: props.hideCapacity ? undefined : waitlistSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [end, regularCapacity, dropinCapacity, waitlistSize, instructors]);

    return (
        <div className={`${styles.Session} ${props.className}`}>
            <div className={styles.title}>
                <span>Session {props.index}</span>
                <div>
                    {!props.hideDelete && <span onClick={props.onDelete} className={`${styles.icon} ${styles.delete}`}></span>}
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.col}>
                    <DatePick
                        label="Start Date"
                        value={start}
                        onChange={(date: any) => setStart(date)}
                        showTimeSelect={true}
                    />
                </div>
                <div className={styles.col}>
                    <DatePick
                        label="End Date"
                        value={end}
                        onChange={(date: any) => setEnd(date)}
                        showTimeSelect={true}
                        minDate={start}
                    />
                </div>
            </div>
            <div className={styles.row}>
                <div className={styles.col}>
                    <Input
                        required={true}
                        label="Instructor First Name"
                        type="text"
                        placeholder={"Instructor First Name (Session " + props.index + ")"}
                        value={instructors && instructors.length > 0 ? instructors[0]?.first_name || "" : ""}
                        onChange={(e) => updateInstructor(0, "first_name", e.target.value)}
                    />
                </div>
                <div className={styles.col}>
                    <Input
                        required={true}
                        label="Instructor Last Name"
                        type="text"
                        placeholder={"Instructor Last Name (Session " + props.index + ")"}
                        value={instructors && instructors.length > 0 ? instructors[0]?.last_name || "" : ""}
                        onChange={(e) => updateInstructor(0, "last_name", e.target.value)}
                    />
                </div>
            </div>
            {!props.hideCapacity && <div className={styles.row}>
                <div className={styles.col}>
                    <Input
                        required={true}
                        label="Capacity"
                        type="number"
                        value={regularCapacity.toString()}
                        onChange={(e) => {
                            const val = parseInt(e.target.value);
                            const next = isNaN(val) ? 0 : val;
                            setRegularCapacity(next);
                            setDropinCapacity(next);
                        }}
                    />
                </div>
                <div className={styles.col}>
                    <Input
                        label="Waitlist size"
                        type="number"
                        value={String(waitlistSize)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            setWaitlistSize(isNaN(val) ? 0 : val);
                        }}
                    />
                </div>
            </div>}
        </div>
    );
};

export default Session;